import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  daoTransactionAdyenFinalizePost,
  daoTransactionAdyenPreparePost,
  daoTransactionAdyenUseCcPayPost,
  daoTransactionAdyenUseCcPaySCAPost,
} from 'dao/transaction-adyen-dao';
import { Action } from 'types/action';
import { Thunk } from 'types/root';
import { TransactionAdyenState } from 'types/transaction-adyen';
import {
  FinalizeAdyenPayload,
  FinalizeAdyenResponse,
  PrepareAdyenPayload,
  PrepareAdyenResp,
  UseAdyenCCPayPayload,
  UseAdyenCCPayResp,
  UseAdyenCCPaySCAPayload,
} from 'types/transaction-adyen';

// Prepare Adyen Transaction
export const postTransactionAdyenPrepare = createAsyncThunk<PrepareAdyenResp, PrepareAdyenPayload, Thunk>(
  'transactions/adyenPrepare',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const paymentMethod = payload.paymentMethod;
    const data = {
      paymentMethodKey: payload.paymentMethodKey,
      browserLocale: payload.browserLocale,
      returnUrl: payload.returnUrl,
    };
    const response = await daoTransactionAdyenPreparePost(accountKey, sessionId, data, paymentMethod);
    return response;
  },
);

// Finalize Adyen Transaction
export const postTransactionAdyenFinalize = createAsyncThunk<FinalizeAdyenResponse, FinalizeAdyenPayload, Thunk>(
  'transactions/adyenFinalize',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const paymentMethod = payload.paymentMethod;
    const data = {
      checkoutSessionId: payload.checkoutSessionId,
      checkoutSessionResult: payload.checkoutSessionResult,
      setAsDefault: payload.setAsDefault,
    };
    const response = await daoTransactionAdyenFinalizePost(accountKey, sessionId, data, paymentMethod);
    return response;
  },
);

// Use Adyen's stored credit card to pay
export const postTransactionAdyenUseCcPay = createAsyncThunk<UseAdyenCCPayResp, UseAdyenCCPayPayload, Thunk>(
  'transactions/adyenUseCcPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAdyenUseCcPayPost(accountKey, sessionId, payload);
    return response;
  },
);

export const postTransactionAdyenUseCcScaPay = createAsyncThunk<UseAdyenCCPayResp, UseAdyenCCPaySCAPayload, Thunk>(
  'transactions/adyenUseCcScaPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAdyenUseCcPaySCAPost(accountKey, sessionId, payload);
    return response;
  },
);

const initialState: TransactionAdyenState = {
  isLoading: false,
  prepareAdyenResp: {} as PrepareAdyenResp,
  isAdyenLoading: false,
};

const transactionsAdyenSlice = createSlice({
  name: 'transactions-adyen',
  initialState,
  reducers: {
    setAdyenCCLoading(state, action) {
      state.isAdyenLoading = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Prepare case
      .addCase(postTransactionAdyenPrepare.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionAdyenPrepare.fulfilled, (state, action: Action) => {
        state.isLoading = false;
        state.prepareAdyenResp = action.payload;
      })
      .addCase(postTransactionAdyenPrepare.rejected, (state) => {
        state.isLoading = false;
        state.prepareAdyenResp = {} as PrepareAdyenResp;
      })
      .addCase(postTransactionAdyenFinalize.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionAdyenFinalize.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(postTransactionAdyenFinalize.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setAdyenCCLoading } = transactionsAdyenSlice.actions;

export default transactionsAdyenSlice.reducer;
